import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import './App.css';
import Proposals from './Proposals';
import Whitepaper from './Whitepaper';

// ── Contract addresses (Sepolia) ─────────────────────────────────────────────
const TBC_ADDRESS      = '0x9d47Db99CEad9F907E0FF99326452C25C8708ACF';
const PI_ADDRESS       = '0xdBE9FBc2584978b1eeA4844bD33a1FE064720520';
const GOV_ADDRESS      = '0x9c36e515bd49AC146f3060e34eccC086007EECa6';
const ORACLE_ADDRESS   = '0xBDEC1a10925AF10790233F87194EB20f85Db9b54';
const SEPOLIA_CHAIN_ID = '0xaa36a7';

// ── Minimal ABIs ─────────────────────────────────────────────────────────────
const TBC_ABI = [
  'function name() view returns (string)',
  'function symbol() view returns (string)',
  'function totalSupply() view returns (uint256)',
  'function balanceOf(address) view returns (uint256)',
  'function S_MAX() view returns (uint256)',
  'function remainingMintable() view returns (uint256)',
  'function supplyPercent() view returns (uint256)',
  'function totalMinted() view returns (uint256)',
  'function totalBurned() view returns (uint256)',
  'function approve(address,uint256) returns (bool)',
];
const PI_ABI = [
  'function currentEpoch() view returns (uint256)',
  'function alpha() view returns (uint256)',
  'function beta() view returns (uint256)',
  'function gamma() view returns (uint256)',
  'function epsilon() view returns (uint256)',
  'function RHO() view returns (uint256)',
  'function totalControllerMinted() view returns (uint256)',
  'function totalControllerBurned() view returns (uint256)',
  'function getRecentEpochs(uint256) view returns (tuple(uint256 epoch,uint256 blockNumber,int256 price,int256 deltaS,uint256 supplyAfter,int256 proportional,int256 velocity,int256 integral,bool minted)[])',
];
const GOV_ABI = [
  'function stakedBalance(address) view returns (uint256)',
  'function totalStaked() view returns (uint256)',
  'function votingWeight(address) view returns (uint256)',
  'function stake(uint256)',
  'function unstake(uint256)',
];
const ORACLE_ABI = [
  'function getBTCPriceUSD() view returns (uint256)',
  'function getLatestPrice() view returns (int256 price, int256 tbcPrice, uint256 updatedAt)',
  'function settleEpochWithOracle()',
];

// ── Types ────────────────────────────────────────────────────────────────────
interface TokenStats {
  supply: string;
  sMax: string;
  supplyPct: number;
  remaining: string;
  totalMinted: string;
  totalBurned: string;
  balance: string;
}
interface PIStats {
  epoch: string;
  alpha: string;
  beta: string;
  gamma: string;
  epsilon: string;
  rho: string;
  minted: string;
  burned: string;
}
interface EpochRecord {
  epoch: string;
  price: string;
  deltaS: bigint;
  supplyAfter: string;
  minted: boolean;
}
interface OracleData {
  btcUSD: string;
  tbcPrice: string;
  updatedAt: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (wei: bigint, decimals = 2) =>
  parseFloat(ethers.formatEther(wei)).toLocaleString(undefined, { maximumFractionDigits: decimals });

const shortAddr = (addr: string) => `${addr.slice(0, 6)}...${addr.slice(-4)}`;

// ─────────────────────────────────────────────────────────────────────────────
export default function App() {
  const [activeTab, setActiveTab]   = useState<'dashboard' | 'proposals' | 'whitepaper'>('dashboard');
  const [provider, setProvider]     = useState<ethers.BrowserProvider | null>(null);
  const [signer, setSigner]         = useState<ethers.JsonRpcSigner | null>(null);
  const [account, setAccount]       = useState('');
  const [chainOk, setChainOk]       = useState(false);
  const [tokenStats, setTokenStats] = useState<TokenStats | null>(null);
  const [piStats, setPIStats]       = useState<PIStats | null>(null);
  const [epochs, setEpochs]         = useState<EpochRecord[]>([]);
  const [staked, setStaked]         = useState('0');
  const [voteWeight, setVoteWeight] = useState('0');
  const [totalStaked, setTotalStaked] = useState('0');
  const [stakeInput, setStakeInput] = useState('');
  const [unstakeInput, setUnstakeInput] = useState('');
  const [oracleData, setOracleData] = useState<OracleData | null>(null);
  const [settling, setSettling]     = useState(false);
  const [loading, setLoading]       = useState(false);
  const [msg, setMsg]               = useState('');

  // ── Connect wallet ──────────────────────────────────────────────────────────
  const connectWallet = async () => {
    if (!(window as any).ethereum) {
      alert('MetaMask not found. Please install it.');
      return;
    }
    const p = new ethers.BrowserProvider((window as any).ethereum);
    await p.send('eth_requestAccounts', []);
    const network = await p.getNetwork();
    if (network.chainId !== BigInt(11155111)) {
      try {
        await (window as any).ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: SEPOLIA_CHAIN_ID }],
        });
      } catch {
        alert('Please switch MetaMask to Sepolia testnet.');
        return;
      }
    }
    const s = await p.getSigner();
    const addr = await s.getAddress();
    setProvider(p);
    setSigner(s);
    setAccount(addr);
    setChainOk(true);
  };

  // ── Load data ───────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!provider || !account) return;
    try {
      const tbc = new ethers.Contract(TBC_ADDRESS, TBC_ABI, provider);
      const pi  = new ethers.Contract(PI_ADDRESS,  PI_ABI,  provider);
      const gov = new ethers.Contract(GOV_ADDRESS, GOV_ABI, provider);

      const [supply, sMax, supplyPct, remaining, totalMinted, totalBurned, balance] =
        await Promise.all([
          tbc.totalSupply(), tbc.S_MAX(), tbc.supplyPercent(),
          tbc.remainingMintable(), tbc.totalMinted(), tbc.totalBurned(),
          tbc.balanceOf(account),
        ]);

      setTokenStats({
        supply:      fmt(supply),
        sMax:        fmt(sMax),
        supplyPct:   Number(supplyPct) / 100,
        remaining:   fmt(remaining),
        totalMinted: fmt(totalMinted),
        totalBurned: fmt(totalBurned),
        balance:     fmt(balance, 4),
      });

      const [epoch, alpha, beta, gamma, epsilon, rho, piMinted, piBurned] =
        await Promise.all([
          pi.currentEpoch(), pi.alpha(), pi.beta(), pi.gamma(),
          pi.epsilon(), pi.RHO(), pi.totalControllerMinted(), pi.totalControllerBurned(),
        ]);

      setPIStats({
        epoch:   epoch.toString(),
        alpha:   (Number(alpha) / 1e6).toFixed(4),
        beta:    (Number(beta)  / 1e6).toFixed(4),
        gamma:   (Number(gamma) / 1e6).toFixed(4),
        epsilon: (Number(epsilon) / 1e6).toFixed(4),
        rho:     (Number(rho) / 1e6).toFixed(4),
        minted:  fmt(piMinted),
        burned:  fmt(piBurned),
      });

      const hist = await pi.getRecentEpochs(5);
      setEpochs(hist.map((r: any) => ({
        epoch:      r.epoch.toString(),
        price:      `$${(Number(r.price) / 1e6).toFixed(4)}`,
        deltaS:     r.deltaS,
        supplyAfter: fmt(r.supplyAfter),
        minted:     r.minted,
      })));

      const [stBal, vw, ts] = await Promise.all([
        gov.stakedBalance(account),
        gov.votingWeight(account),
        gov.totalStaked(),
      ]);
      setStaked(fmt(stBal, 4));
      setVoteWeight(vw.toString());
      setTotalStaked(fmt(ts));

      // ── Oracle data ────────────────────────────────────────────────────────
      try {
        const oracle = new ethers.Contract(ORACLE_ADDRESS, ORACLE_ABI, provider);
        const [btcUSD, [, tbcPrice, updatedAt]] = await Promise.all([
          oracle.getBTCPriceUSD(),
          oracle.getLatestPrice(),
        ]);
        setOracleData({
          btcUSD:    `$${Number(btcUSD).toLocaleString()}`,
          tbcPrice:  `$${(Number(tbcPrice) / 1e6).toFixed(8)}`,
          updatedAt: new Date(Number(updatedAt) * 1000).toLocaleTimeString(),
        });
      } catch { /* oracle may not be set up */ }
    } catch (e) {
      console.error(e);
    }
  }, [provider, account]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Stake ───────────────────────────────────────────────────────────────────
  const handleStake = async () => {
    if (!signer || !stakeInput) return;
    setLoading(true); setMsg('');
    try {
      const tbc = new ethers.Contract(TBC_ADDRESS, TBC_ABI, signer);
      const gov = new ethers.Contract(GOV_ADDRESS, GOV_ABI, signer);
      const amount = ethers.parseEther(stakeInput);
      const tx1 = await tbc.approve(GOV_ADDRESS, amount);
      setMsg('Approving...');
      await tx1.wait();
      const tx2 = await gov.stake(amount);
      setMsg('Staking...');
      await tx2.wait();
      setMsg('✅ Staked successfully!');
      setStakeInput('');
      await loadData();
    } catch (e: any) {
      setMsg(`❌ ${e.reason || e.message}`);
    }
    setLoading(false);
  };

  // ── Unstake ─────────────────────────────────────────────────────────────────
  const handleUnstake = async () => {
    if (!signer || !unstakeInput) return;
    setLoading(true); setMsg('');
    try {
      const gov = new ethers.Contract(GOV_ADDRESS, GOV_ABI, signer);
      const tx = await gov.unstake(ethers.parseEther(unstakeInput));
      setMsg('Unstaking...');
      await tx.wait();
      setMsg('✅ Unstaked successfully!');
      setUnstakeInput('');
      await loadData();
    } catch (e: any) {
      setMsg(`❌ ${e.reason || e.message}`);
    }
    setLoading(false);
  };

  // ── Settle epoch via oracle ─────────────────────────────────────────────────
  const handleSettle = async () => {
    if (!signer) return;
    setSettling(true); setMsg('');
    try {
      const oracle = new ethers.Contract(ORACLE_ADDRESS, ORACLE_ABI, signer);
      const tx = await oracle.settleEpochWithOracle();
      setMsg('⏳ Settling epoch with live BTC price...');
      await tx.wait();
      setMsg('✅ Epoch settled! Supply updated by PI controller.');
      await loadData();
    } catch (e: any) {
      setMsg(`❌ ${e.reason || e.message}`);
    }
    setSettling(false);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="app">
      {/* ── Navbar ── */}
      <nav className="navbar">
        <div className="navbar-brand">
          <span className="navbar-logo">₿</span>
          <div>
            <div className="navbar-title">TBC</div>
            <div className="navbar-subtitle">Twin Bitcoin · Sepolia</div>
          </div>
        </div>
        {account ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="wallet-address">{shortAddr(account)}</span>
            <button className="connect-btn connected">Connected</button>
          </div>
        ) : (
          <button className="connect-btn" onClick={connectWallet}>Connect MetaMask</button>
        )}
      </nav>

      {/* ── Hero ── */}
      <div className="hero">
        <div className="hero-badge">⚡ LIVE ON SEPOLIA TESTNET</div>
        <h1>Twin <span>Bitcoin</span></h1>
        <p>Scarcity-anchored adaptive supply cryptocurrency — 2× Bitcoin's cap with on-chain PI control.</p>
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: 4, maxWidth: 1100, margin: '0 auto 32px', padding: '0 24px' }}>
        {(['dashboard', 'proposals', 'whitepaper'] as const).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            disabled={tab !== 'whitepaper' && !chainOk}
            style={{ padding: '10px 24px', borderRadius: 10, border: 'none', cursor: (tab !== 'whitepaper' && !chainOk) ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 14, transition: 'all 0.2s',
              background: activeTab === tab ? 'linear-gradient(135deg, #f7931a, #e8820a)' : 'rgba(255,255,255,0.05)',
              color: activeTab === tab ? '#fff' : '#94a3b8',
              opacity: (tab !== 'whitepaper' && !chainOk) ? 0.4 : 1 }}>
            {tab === 'dashboard' ? '📊 Dashboard' : tab === 'proposals' ? '🗳️ Proposals' : '📄 Whitepaper'}
          </button>
        ))}
      </div>

      {activeTab === 'whitepaper' ? (
        <Whitepaper />
      ) : !chainOk ? (
        <div className="not-connected">
          <div className="icon">🦊</div>
          <p>Connect MetaMask to view live contract data</p>
        </div>
      ) : activeTab === 'proposals' ? (
        <Proposals signer={signer} provider={provider} account={account} />
      ) : (
        <>
          {/* ── Stats Grid ── */}
          {tokenStats && (
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-label">Total Supply</div>
                <div className="stat-value orange">{tokenStats.supply}</div>
                <div className="stat-sub">TBC in circulation</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Max Supply (S_max)</div>
                <div className="stat-value">{tokenStats.sMax}</div>
                <div className="stat-sub">Hard cap — 2× Bitcoin</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Your Balance</div>
                <div className="stat-value green">{tokenStats.balance}</div>
                <div className="stat-sub">TBC in your wallet</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">PI Epoch</div>
                <div className="stat-value blue">#{piStats?.epoch ?? '—'}</div>
                <div className="stat-sub">Epochs settled</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Total Minted</div>
                <div className="stat-value green">{tokenStats.totalMinted}</div>
                <div className="stat-sub">TBC minted by PI controller</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Total Burned</div>
                <div className="stat-value" style={{ color: '#ef4444' }}>{tokenStats.totalBurned}</div>
                <div className="stat-sub">TBC burned by PI controller</div>
              </div>
            </div>
          )}

          {/* ── Chainlink Oracle Panel ── */}
          {oracleData && (
            <div style={{ maxWidth: 1100, margin: '0 auto 24px', padding: '0 24px' }}>
              <div className="panel" style={{ background: 'linear-gradient(135deg, rgba(247,147,26,0.08), rgba(59,130,246,0.05))', border: '1px solid rgba(247,147,26,0.2)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 22 }}>⛓️</span>
                      <div>
                        <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Chainlink BTC/USD</div>
                        <div style={{ fontSize: 24, fontWeight: 800, color: '#f7931a' }}>{oracleData.btcUSD}</div>
                      </div>
                    </div>
                    <div style={{ width: 1, height: 40, background: 'rgba(255,255,255,0.08)' }} />
                    <div>
                      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>TBC Derived Price</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#10b981' }}>{oracleData.tbcPrice}</div>
                    </div>
                    <div style={{ width: 1, height: 40, background: 'rgba(255,255,255,0.08)' }} />
                    <div>
                      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Last Updated</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#94a3b8' }}>{oracleData.updatedAt}</div>
                    </div>
                  </div>
                  <button
                    className="action-btn primary"
                    onClick={handleSettle}
                    disabled={settling}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {settling ? '⏳ Settling...' : '⚡ Settle Epoch'}
                  </button>
                </div>
                <div style={{ marginTop: 10, fontSize: 12, color: '#475569' }}>
                  Oracle: <a href={`https://sepolia.etherscan.io/address/${ORACLE_ADDRESS}`} target="_blank" rel="noreferrer" style={{ color: '#3b82f6' }}>{ORACLE_ADDRESS}</a>
                </div>
                {msg && <div className={msg.startsWith('✅') ? 'success-msg' : msg.startsWith('❌') ? 'error-msg' : 'loading'} style={{ marginTop: 10 }}>{msg}</div>}
              </div>
            </div>
          )}

          {/* ── Supply Bar ── */}
          {tokenStats && (
            <div className="supply-bar-wrap">
              <div className="supply-bar-card">
                <div className="supply-bar-header">
                  <span>Supply Utilisation</span>
                  <span style={{ color: '#f7931a', fontWeight: 700 }}>{tokenStats.supplyPct.toFixed(4)}%</span>
                </div>
                <div className="supply-bar-track">
                  <div className="supply-bar-fill" style={{ width: `${tokenStats.supplyPct}%` }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#475569', marginTop: 8 }}>
                  <span>0 TBC</span>
                  <span>Remaining: {tokenStats.remaining} TBC</span>
                  <span>42,000,000 TBC</span>
                </div>
              </div>
            </div>
          )}

          {/* ── Two-col: PI Params + Epoch History ── */}
          <div className="two-col">
            <div className="panel">
              <h3>⚙️ PI Controller Parameters</h3>
              {piStats ? (
                <>
                  <div className="param-row"><span className="param-name">α (alpha) — P gain</span><span className="param-val">{piStats.alpha}</span></div>
                  <div className="param-row"><span className="param-name">β (beta) — Velocity gain</span><span className="param-val">{piStats.beta}</span></div>
                  <div className="param-row"><span className="param-name">γ (gamma) — Integral gain</span><span className="param-val">{piStats.gamma}</span></div>
                  <div className="param-row"><span className="param-name">ε (epsilon) — Anti-windup</span><span className="param-val">{piStats.epsilon}</span></div>
                  <div className="param-row">
                    <span className="param-name">ρ (rho) — Rate limit <span className="param-immutable">IMMUTABLE</span></span>
                    <span className="param-val">{piStats.rho}</span>
                  </div>
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#64748b' }}>
                    <span>Controller minted: <b style={{ color: '#10b981' }}>{piStats.minted} TBC</b></span>
                    <span>Burned: <b style={{ color: '#ef4444' }}>{piStats.burned} TBC</b></span>
                  </div>
                </>
              ) : <span className="loading">Loading...</span>}
            </div>

            <div className="panel">
              <h3>📜 Recent Epoch History</h3>
              {epochs.length === 0 ? (
                <div className="not-connected" style={{ padding: '20px 0' }}>
                  <p>No epochs settled yet</p>
                </div>
              ) : (
                epochs.map((e, i) => {
                  const action = e.deltaS > BigInt(0) ? 'mint' : e.deltaS < BigInt(0) ? 'burn' : 'hold';
                  return (
                    <div className="epoch-row" key={i}>
                      <span className="epoch-num">#{e.epoch}</span>
                      <span className="epoch-price">{e.price}</span>
                      <span className={`epoch-action ${action}`}>{action.toUpperCase()}</span>
                      <span className="epoch-supply">{e.supplyAfter}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* ── Two-col: Stake + Governance ── */}
          <div className="two-col">
            <div className="panel">
              <h3>🔒 Stake TBC for Governance</h3>
              <div className="input-row">
                <input
                  className="tbc-input"
                  placeholder="Amount to stake"
                  value={stakeInput}
                  onChange={e => setStakeInput(e.target.value)}
                  type="number"
                  min="0"
                />
                <button className="action-btn primary" onClick={handleStake} disabled={loading || !stakeInput}>
                  Stake
                </button>
              </div>
              <div className="input-row">
                <input
                  className="tbc-input"
                  placeholder="Amount to unstake"
                  value={unstakeInput}
                  onChange={e => setUnstakeInput(e.target.value)}
                  type="number"
                  min="0"
                />
                <button className="action-btn secondary" onClick={handleUnstake} disabled={loading || !unstakeInput}>
                  Unstake
                </button>
              </div>
              {msg && <div className={msg.startsWith('✅') ? 'success-msg' : msg.startsWith('❌') ? 'error-msg' : 'loading'}>{msg}</div>}
            </div>

            <div className="panel">
              <h3>🗳️ Your Governance Power</h3>
              <div className="param-row"><span className="param-name">Your Staked TBC</span><span className="param-val">{staked} TBC</span></div>
              <div className="param-row"><span className="param-name">Voting Weight (√staked)</span><span className="param-val">{voteWeight} votes</span></div>
              <div className="param-row"><span className="param-name">Total TBC Staked</span><span className="param-val">{totalStaked} TBC</span></div>
              <div style={{ marginTop: 20, padding: 14, background: 'rgba(247,147,26,0.05)', borderRadius: 10, border: '1px solid rgba(247,147,26,0.1)', fontSize: 13, color: '#94a3b8', lineHeight: 1.6 }}>
                💡 Stake 1,000+ TBC to submit proposals. Voting uses <b style={{ color: '#f7931a' }}>quadratic math</b> — weight = √(staked TBC) to prevent whale dominance.
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Footer ── */}
      <footer className="footer">
        <p>
          TBC Token: <a href={`https://sepolia.etherscan.io/address/${TBC_ADDRESS}`} target="_blank" rel="noreferrer">{shortAddr(TBC_ADDRESS)}</a>
          {' · '}
          PI Controller: <a href={`https://sepolia.etherscan.io/address/${PI_ADDRESS}`} target="_blank" rel="noreferrer">{shortAddr(PI_ADDRESS)}</a>
          {' · '}
          Governance: <a href={`https://sepolia.etherscan.io/address/${GOV_ADDRESS}`} target="_blank" rel="noreferrer">{shortAddr(GOV_ADDRESS)}</a>
          {' · '}
          Oracle: <a href={`https://sepolia.etherscan.io/address/${ORACLE_ADDRESS}`} target="_blank" rel="noreferrer">{shortAddr(ORACLE_ADDRESS)}</a>
        </p>
        <p style={{ marginTop: 8 }}>Twin Bitcoin © 2026 · Sepolia Testnet</p>
      </footer>
    </div>
  );
}
